import { useState, useEffect, useCallback } from 'react';
import {
  Mail, Plus, Save, Star, Trash2, Loader2, AlertCircle, CheckCircle2, Inbox,
} from 'lucide-react';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { cn } from '../../../foundation/altzero_base/base/lib/utils';
import { HtmlBodyEditor } from '../components/common/HtmlBodyEditor';
import { EMAIL_TOKEN_GROUPS } from '../lib/reportTokens';
import { bvReportService, type BvEmailTemplate } from '../services/bvReportService';

const STARTER_SUBJECT = 'Bill Validation Report — {nmi} — {billing_period}';

const STARTER_BODY = `<p>Hi {client_name},</p>
<p>Please find attached the bill validation report for your {billing_period} invoice for NMI {nmi}.</p>
<p>Expected invoice total: <strong>{expected_total}</strong><br>
Retailer invoice total: <strong>{invoiced_total}</strong><br>
Variance: <strong>{variance_dollars} ({variance_percent})</strong></p>
<p>{overcharge_summary}</p>
<p>If you have any questions about the figures, reply to this email and we'll walk you through them.</p>
<p>Kind regards,<br>Choice Energy</p>`;

type Draft = {
  id: string | null;
  name: string;
  subject: string;
  body_html: string;
  is_default: boolean;
};

const EMPTY_DRAFT: Draft = { id: null, name: '', subject: '', body_html: '', is_default: false };

/**
 * Covering-email templates for bill validation reports.
 *
 * Lives inside Bill Validation rather than under Tender → Email, so an adviser
 * who only works on bills never has to go into the tender section to edit the
 * email that carries their report. It writes to the same `email_templates`
 * table, confined to the `bill_validation` category — the server filters on both
 * org and category, so nothing here can reach a tender template.
 *
 * Nothing edited here can change a figure: the numbers are filled from the bill
 * at send time, and the report PDF is captured from the report screen.
 */
export const BvEmailTemplatesPage = () => {
  const [templates, setTemplates] = useState<BvEmailTemplate[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);

  const load = useCallback(async (): Promise<BvEmailTemplate[]> => {
    const res = await bvReportService.listEmailTemplates();
    if (res.success) {
      setTemplates(res.data ?? []);
      setListError(null);
      return res.data ?? [];
    }
    setListError(res.error ?? 'Could not load the email templates');
    return [];
  }, []);

  useEffect(() => {
    let mounted = true;
    void (async () => {
      const list = await load();
      if (!mounted) return;
      // Open the default (or the first) straight away — an editor sitting empty
      // beside a populated list is a click nobody needs to make.
      const first = list.find((t) => t.is_default) ?? list[0];
      if (first) setDraft({ ...first });
      setListLoading(false);
    })();
    return () => { mounted = false; };
  }, [load]);

  const startNew = () => {
    setDraft({
      ...EMPTY_DRAFT,
      name: 'Bill validation report',
      subject: STARTER_SUBJECT,
      body_html: STARTER_BODY,
      is_default: templates.length === 0,
    });
    setSaveError(null);
  };

  const patch = (p: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...p } : d));

  const handleSave = async () => {
    if (!draft) return;
    if (!draft.name.trim()) {
      setSaveError('Give the template a name before saving');
      return;
    }
    setSaving(true);
    setSaveError(null);

    const res = draft.id
      ? await bvReportService.updateEmailTemplate(draft.id, {
          name: draft.name, subject: draft.subject, body_html: draft.body_html, is_default: draft.is_default,
        })
      : await bvReportService.createEmailTemplate({
          name: draft.name, subject: draft.subject, body_html: draft.body_html, is_default: draft.is_default,
        });

    if (res.success && res.data) {
      setDraft({ ...res.data });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
      await load();
    } else {
      setSaveError(res.error ?? 'Could not save the template');
    }
    setSaving(false);
  };

  const handleDelete = async (t: BvEmailTemplate) => {
    if (!window.confirm(`Delete "${t.name}"? Reports already sent with it are unaffected.`)) return;
    const res = await bvReportService.deleteEmailTemplate(t.id);
    if (res.success) {
      if (draft?.id === t.id) setDraft(null);
      await load();
    } else {
      setListError(res.error ?? 'Could not delete the template');
    }
  };

  const handleMakeDefault = async (t: BvEmailTemplate) => {
    const res = await bvReportService.updateEmailTemplate(t.id, { is_default: true });
    if (res.success) {
      if (draft?.id === t.id) patch({ is_default: true });
      await load();
    } else {
      setListError(res.error ?? 'Could not set the default template');
    }
  };

  /** Appends a token to the subject — the body has its own insert affordance. */
  const insertIntoSubject = (key: string) => patch({ subject: `${draft?.subject ?? ''}{${key}}` });

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Templates</h1>
          <p className="text-sm text-gray-500 mt-1">
            The covering email that carries a bill validation report to a client. The report PDF
            itself is generated from the bill — nothing here changes a figure.
          </p>
        </div>
        <Button onClick={startNew} className="gap-2">
          <Plus className="h-4 w-4" /> New template
        </Button>
      </div>

      {listError && (
        <div className="flex gap-2 items-start rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <AlertCircle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
          <p className="text-sm text-red-800 break-words">{listError}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5 items-start">

        {/* ── List ── */}
        <div className="border border-gray-200 rounded-xl bg-white overflow-hidden">
          <div className="px-4 py-2.5 border-b bg-gray-50">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Templates {templates.length > 0 && `(${templates.length})`}
            </h2>
          </div>

          {listLoading && templates.length === 0 ? (
            <div className="p-4 space-y-2">
              <div className="h-12 bg-gray-100 animate-pulse rounded-lg" />
              <div className="h-12 bg-gray-100 animate-pulse rounded-lg" />
            </div>
          ) : templates.length === 0 ? (
            <div className="p-6 text-center">
              <Inbox className="h-8 w-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-600">No templates yet</p>
              <p className="text-xs text-gray-400 mt-1 mb-3">
                Reports still send, using a built-in standard email.
              </p>
              <Button size="sm" onClick={startNew} className="gap-1.5">
                <Plus className="h-3.5 w-3.5" /> Create the first one
              </Button>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {templates.map((t) => (
                <li key={t.id} className={cn('group px-3 py-2.5', draft?.id === t.id && 'bg-blue-50')}>
                  <button
                    type="button"
                    onClick={() => { setDraft({ ...t }); setSaveError(null); }}
                    className="w-full text-left"
                  >
                    <span className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-sm font-medium text-gray-800">{t.name}</span>
                      {t.is_default && (
                        <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                          Default
                        </span>
                      )}
                    </span>
                    <span className="block text-xs text-gray-400 truncate mt-0.5">{t.subject || 'No subject'}</span>
                  </button>
                  <div className="flex items-center gap-3 mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    {!t.is_default && (
                      <button
                        type="button"
                        onClick={() => void handleMakeDefault(t)}
                        className="text-[11px] text-gray-500 hover:text-amber-600 flex items-center gap-1"
                      >
                        <Star className="h-3 w-3" /> Make default
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => void handleDelete(t)}
                      className="text-[11px] text-gray-500 hover:text-red-600 flex items-center gap-1"
                    >
                      <Trash2 className="h-3 w-3" /> Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ── Editor ── */}
        {!draft ? (
          <div className="border-2 border-dashed border-gray-200 rounded-xl p-14 text-center">
            <Mail className="h-9 w-9 text-gray-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-500">No template open</p>
            <p className="text-xs text-gray-400 mt-1">Pick one from the list, or create a new one.</p>
          </div>
        ) : (
          <div className="border border-gray-200 rounded-xl bg-white p-5 space-y-4">

            {saveError && (
              <div className="flex gap-2 items-start rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
                <AlertCircle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
                <p className="text-xs text-red-800 break-words">{saveError}</p>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Template name</label>
                <input
                  value={draft.name}
                  onChange={(e) => patch({ name: e.target.value })}
                  placeholder="Bill validation report"
                  className="w-full text-sm border border-gray-200 rounded-md px-2.5 py-2"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700 sm:mt-6">
                <input
                  type="checkbox"
                  checked={draft.is_default}
                  onChange={(e) => patch({ is_default: e.target.checked })}
                />
                Use this one by default when sending a report
              </label>
            </div>

            <div>
              <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                <label className="block text-xs font-medium text-gray-600">Subject</label>
                <TokenPicker onPick={insertIntoSubject} label="Insert into subject" />
              </div>
              <input
                value={draft.subject}
                onChange={(e) => patch({ subject: e.target.value })}
                placeholder={STARTER_SUBJECT}
                className="w-full text-sm border border-gray-200 rounded-md px-2.5 py-2 font-mono"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Message</label>
              <HtmlBodyEditor
                value={draft.body_html}
                onChange={(html) => patch({ body_html: html })}
                heightClass="h-[420px]"
              />
              <p className="text-[11px] text-gray-400 mt-1.5">
                Edit directly in <strong>Design</strong>, or switch to <strong>HTML</strong> for the source.
                Type placeholders like <code className="font-mono">{'{client_name}'}</code> anywhere —
                they fill from the bill when the report is sent.
              </p>
            </div>

            <TokenReference />

            <div className="flex items-center gap-3 pt-1">
              <Button onClick={() => void handleSave()} disabled={saving} className="gap-2">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {saving ? 'Saving…' : draft.id ? 'Save changes' : 'Create template'}
              </Button>
              {savedFlash && (
                <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Saved
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Token helpers ────────────────────────────────────────────────────────────

type TokenPickerProps = { onPick: (key: string) => void; label: string };

const TokenPicker = ({ onPick, label }: TokenPickerProps) => (
  <select
    value=""
    onChange={(e) => { if (e.target.value) onPick(e.target.value); e.target.value = ''; }}
    className="text-[11px] border border-gray-200 rounded px-1.5 py-1 bg-white text-gray-600"
  >
    <option value="">{label}…</option>
    {EMAIL_TOKEN_GROUPS.map(({ group, tokens }) => (
      <optgroup key={group} label={group}>
        {tokens.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
      </optgroup>
    ))}
  </select>
);

const TokenReference = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-blue-100 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-2 bg-blue-50 text-xs font-medium text-blue-700 hover:bg-blue-100"
      >
        <span>Available placeholders</span>
        <span>{open ? '−' : '+'}</span>
      </button>
      {open && (
        <div className="p-3 space-y-3 bg-white">
          {EMAIL_TOKEN_GROUPS.map(({ group, tokens }) => (
            <div key={group}>
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">{group}</p>
              <div className="flex flex-wrap gap-1">
                {tokens.map((t) => (
                  <span
                    key={t.key}
                    title={t.label}
                    className="px-1.5 py-0.5 text-[11px] font-mono bg-gray-50 text-gray-700 border border-gray-200 rounded"
                  >
                    {'{' + t.key + '}'}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
